import { useEffect } from 'react';

import type { RumEvent, Site } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';

import useUser from '@/utils/hooks/swr/useUser';

import { LOCAL } from '@/lib/environments';
import { ERROR_FILTERS } from './const';

/**
 * Checks if an error event matches any of the configured filters.
 * Supports pattern matching on message, stack, and source URL.
 */
const shouldFilterError = (event: RumEvent): boolean => {
    if (event.type !== 'error') {
        return false;
    }

    const errorEvent = event as RumEvent & {
        error?: { message?: string; stack?: string; source?: string };
        view?: { url?: string };
    };

    const errorMessage = errorEvent.error?.message || '';
    const errorStack = errorEvent.error?.stack || '';
    const errorSource = errorEvent.error?.source || errorEvent.view?.url || '';

    return ERROR_FILTERS.some((filter) => {
        // Check message patterns
        const matchesPattern = filter.patterns?.some(
            (pattern) => errorMessage.includes(pattern) || errorStack.includes(pattern)
        );

        // Check source URL pattern if defined
        const matchesSource = filter.sourcePattern
            ? filter.sourcePattern.test(errorSource) || filter.sourcePattern.test(errorStack)
            : true;

        // For filters with sourcePattern, require both pattern AND source match
        // For filters without sourcePattern, just pattern match is enough
        return filter.sourcePattern ? matchesPattern && matchesSource : matchesPattern;
    });
};

export const DataDogErrorMonitoring = () => {
    const { user } = useUser({ revalidateOnMount: false });

    useEffect(() => {
        if (process.env.NEXT_PUBLIC_ENV === LOCAL) {
            return;
        }

        const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
        const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
        const site = process.env.NEXT_PUBLIC_DATADOG_SITE as Site | undefined;
        const baseApiUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

        // Guard against missing required credentials to prevent SDK initialization errors
        if (!applicationId || !clientToken || !site) {
            return;
        }

        datadogRum.init({
            applicationId,
            clientToken,
            site,
            service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
            env: process.env.NEXT_PUBLIC_DATADOG_ENV,
            // Use conditional array to prevent matching all URLs with empty string
            allowedTracingUrls: baseApiUrl ? [baseApiUrl] : [],
            // Specify a version number to identify the deployed version of your application in Datadog
            version: process.env.NEXT_PUBLIC_APP_VERSION,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 0,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            /**
             * Filter out transient navigation errors that don't require monitoring.
             * These errors typically occur during client-side navigation when:
             * - Network issues prevent loading static props
             * - User navigates away before data finishes loading
             * - Route changes are cancelled by subsequent navigations
             */
            beforeSend: (event) => {
                if (shouldFilterError(event)) {
                    return false;
                }
                return true;
            }
        });
    }, []);

    useEffect(() => {
        if (process.env.NEXT_PUBLIC_ENV === LOCAL || !user?.id) {
            return;
        }

        datadogRum.setUser({ id: user.id.toString() });
    }, [user]);

    if (process.env.NEXT_PUBLIC_ENV === LOCAL) return null;

    return null;
};
